import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getMaxExamDate, getMinExamDate } from '../utils/examDate';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Reported while the typed date is incomplete, so callers neither save it nor submit an older value.
export const INVALID_EXAM_DATE = 'invalid';

const toDayjs = (value) => (DATE_PATTERN.test(value ?? '') ? dayjs(value) : null);

// Value in/out is a YYYY-MM-DD string ('' when empty), matching what the API stores.
export default function ExamDatePicker({ label, value, onChange, error = false, helperText, disabled = false, size }) {
  const [draft, setDraft] = useState(() => toDayjs(value));
  const [open, setOpen] = useState(false);
  // After the calendar is closed, field clicks type instead of reopening it (e.g. the phone keyboard) until focus leaves.
  const [typingMode, setTypingMode] = useState(false);
  // MUI's Cancel restores its last committed date, which ignores parent updates; remount on those to reset it.
  const [pickerKey, setPickerKey] = useState(0);
  const lastEmittedRef = useRef(value);
  const inputRef = useRef(null);

  // Follow parent updates (e.g. a save response) but keep an in-progress typed date.
  useEffect(() => {
    if (value === INVALID_EXAM_DATE) return;
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      setPickerKey((key) => key + 1);
    }
    const next = toDayjs(value);
    setDraft((current) => (next && current?.isValid() && current.isSame(next, 'day') ? current : next));
  }, [value]);

  const emit = (nextValue) => {
    lastEmittedRef.current = nextValue;
    onChange(nextValue);
  };

  const handleChange = (next) => {
    setDraft(next);
    if (!next) emit('');
    else if (next.isValid()) emit(next.format('YYYY-MM-DD'));
    else emit(INVALID_EXAM_DATE);
  };

  const handleFieldClick = (event) => {
    if (disabled || typingMode || event.target.closest('button')) return;
    setOpen(true);
  };

  const handleFieldBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setTypingMode(false);
    // MUI emits nothing while a date typed from empty is partial (e.g. "15.12.YYYY"), so check the text on leave.
    if (draft) return;
    const hasDigits = /\d/.test(inputRef.current?.value ?? '');
    if (hasDigits && value !== INVALID_EXAM_DATE) emit(INVALID_EXAM_DATE);
    else if (!hasDigits && value === INVALID_EXAM_DATE) emit('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        key={pickerKey}
        inputRef={inputRef}
        label={label}
        value={draft}
        onChange={handleChange}
        minDate={dayjs(getMinExamDate())}
        maxDate={dayjs(getMaxExamDate())}
        format="DD.MM.YYYY"
        // Header click opens years, then months, so far-off dates in the 2-year range are a few taps away.
        views={['year', 'month', 'day']}
        openTo="day"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setTypingMode(true);
        }}
        disabled={disabled}
        disableHighlightToday
        slotProps={{
          textField: { fullWidth: true, size, error, helperText, onClick: handleFieldClick, onBlur: handleFieldBlur },
          field: { clearable: true },
        }}
      />
    </LocalizationProvider>
  );
}
